import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, Menu, X } from "lucide-react";
import { BRAND, BOOKING, IMAGES, NAV_LINKS } from "@/lib/site";

export const Wordmark = ({ testid = "wordmark" }) => (
  <a href="#top" data-testid={testid} className="flex items-center leading-none">
    <img src={IMAGES.logo} alt={`${BRAND.parts[0]} ${BRAND.parts[1]}`} className="h-10 sm:h-12 w-auto select-none" />
  </a>
);

const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 border-b border-line/60 bg-ink/80 backdrop-blur-xl"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Wordmark />

        <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testid}
              className="text-sm tracking-wide text-sand hover:text-cream transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BOOKING.account}
            data-testid="header-member-login"
            className="hidden sm:inline-flex items-center text-xs tracking-wide text-stone2 hover:text-cream transition-colors duration-300 mr-2"
          >
            Member login
          </a>
          <a
            href={BOOKING.book}
            data-testid="header-book-now-button"
            className="hidden sm:inline-flex items-center bg-rust text-white text-xs font-medium uppercase tracking-[0.18em] px-6 py-3 rounded-none hover:bg-rust-dark transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust"
          >
            Book now
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 border border-line text-cream hover:bg-coal transition-colors duration-300"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden overflow-hidden border-t border-line/60 bg-ink/95 backdrop-blur-xl"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base text-sand hover:text-cream border-b border-line/40 transition-colors duration-300"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={BOOKING.account}
                data-testid="mobile-member-login"
                onClick={() => setOpen(false)}
                className="py-3 min-h-[44px] flex items-center text-base text-sand hover:text-cream border-b border-line/40 transition-colors duration-300"
              >
                Member login
              </a>
              <a
                href="/staff"
                data-testid="mobile-staff-link"
                onClick={() => setOpen(false)}
                className="py-3 min-h-[44px] flex items-center gap-2 text-base text-stone2 hover:text-cream border-b border-line/40 transition-colors duration-300"
              >
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                Staff
              </a>
              <a
                href={BOOKING.book}
                data-testid="mobile-book-now-button"
                onClick={() => setOpen(false)}
                className="mt-5 text-center bg-rust text-white text-xs font-medium uppercase tracking-[0.18em] px-6 py-4 hover:bg-rust-dark transition-colors duration-300"
              >
                Book now
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
